import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface DetailDrawerProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Sticky action area rendered below the scrollable body. */
  footer?: ReactNode;
}

/**
 * Right-side detail drawer for queue items. Keeps the queue visible behind a
 * backdrop so operators keep their place while reviewing one item.
 */
export default function DetailDrawer({ open, title, onClose, children, footer }: DetailDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // DM-6208: held in a ref so the effect below depends on `open` ALONE.
  // Every caller passes an inline arrow for onClose, so depending on it re-ran
  // the effect on each render -- and its focus() call pulled focus back to the
  // panel, which meant a footer text field lost focus after a single
  // keystroke, and again on every background refetch.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/60 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="absolute inset-y-0 right-0 w-full max-w-[560px] bg-dark-card border-l border-gray-border flex flex-col animate-fade-in focus:outline-none"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-border">
          <h2 className="text-lg font-semibold text-white truncate pr-4">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-text hover:text-white flex-shrink-0"
            aria-label="Close details"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-4 space-y-4">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-gray-border">{footer}</div>}
      </div>
    </div>
  );
}
